import {assert} from '@one-click-lca/3d-buildings-shared';
import isEqual from 'lodash/isEqual';
import * as THREE from 'three';

import {getRandomColorN} from 'Libs/map3d/three/three_utils';

import {getColorByIndex} from '../components/common/features/project/project_color';
import getConfig from '../load_config';

export function getProjectColor(
  project: {status: string},
  statuses: Array<string>,
): THREE.Color {
  const idx = statuses.findIndex(s => s === project.status);
  return new THREE.Color(getColorByIndex(idx));
}

export function getTenementColor(
  tenement: string,
  tenements: Array<string>,
): string {
  const idx = tenements.findIndex(t => t === tenement);
  return getRandomColorN(idx, true).getStyle();
}

export function hasSameContent<T>(arr1: Array<T>, arr2: Array<T>): boolean {
  return isEqual([...arr1].sort(), [...arr2].sort());
}

export function isProductionServer(): boolean {
  const config = getConfig();

  return (
    // Not sure if we should continue to test window here.
    // before, the isProduction was in this global object... making Node always answer no.
    // Do we want to keep it that way?
    typeof window !== 'undefined' && config.isProduction
  );
}

export function hasTransparency(img: HTMLImageElement): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  assert(ctx);
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 255) {
      return true;
    }
  }
  return false;
}

/**
 * Writes pixel data to a canvas, with the origin at the bottom-left,
 * and in row major order.
 *
 * @param pixels Pixels in RGBA format, 4 bytes per pixel
 */
export async function savePixelsToCanvas(
  pixels: Uint8Array,
  width: number,
  height: number,
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  assert(context);
  const imageData = new ImageData(
    Uint8ClampedArray.from(pixels),
    width,
    height,
  );
  const imageBitmap = await createImageBitmap(imageData);
  context.translate(0, height);
  context.scale(1, -1);
  context.drawImage(imageBitmap, 0, 0);
  context.transform(1, 0, 0, 1, 0, 0);
  imageBitmap.close();
  return canvas;
}

/**
 * Writes pixel data to a file, with the origin at the bottom-left,
 * and in row major order.
 *
 * @param pixels Pixels in RGBA format, 4 bytes per pixel
 */
export async function savePixelsOnClient(
  pixels: Uint8Array,
  width: number,
  height: number,
  filename = 'debugPixels.png',
): Promise<void> {
  saveOnClient(
    (await savePixelsToCanvas(pixels, width, height)).toDataURL(),
    filename,
  );
}

export function saveOnClient(
  dataToSave: Blob | string,
  filename: string,
): void {
  const link = document.createElement('a');
  link.style.display = 'none';
  document.body.appendChild(link); // Firefox workaround, see #6594
  if (dataToSave instanceof Blob) {
    link.href = URL.createObjectURL(dataToSave);
  } else {
    link.href = dataToSave;
  }
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function transformToCamelCase(data: unknown): unknown {
  return JSON.parse(
    JSON.stringify(data).replace(
      /(_\w)\w+":/g,
      match => match[1].toUpperCase() + match.substring(2),
    ),
  );
}

/**
 * Sleep for a given number of milliseconds (precision not guaranteed)
 * @param ms Number of milliseconds to sleep
 * @returns A promise that should be awaited to sleep for the given number of milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Create an array with the given length and fill it using the provided callback.
 * This is here to work around the fact that Array.fill does not work well with non-primitive types,
 * filling with a reference to the same object.
 */
export function filledArray<T>(
  length: number,
  fillCb: (row: number) => T,
): T[] {
  const result: T[] = [];
  for (let r = 0; r < length; r++) {
    result.push(fillCb(r));
  }
  return result;
}

/**
 * Create a 2D array with the given dimensions and fill it using the provided callback.
 * This is here to work around the fact that Array.fill does not work well with non-primitive types,
 * filling with a reference to the same object.
 */
export function filledArray2D<T>(
  rows: number,
  cols: number,
  fillCb: (row: number, col: number) => T,
): T[][] {
  const result: T[][] = [];
  for (let r = 0; r < rows; r++) {
    result.push(filledArray(cols, c => fillCb(r, c)));
  }
  return result;
}

/**
 * Create a 3D array with the given dimensions and fill it using the provided callback.
 * This is here to work around the fact that Array.fill does not work well with non-primitive types,
 * filling with a reference to the same object.
 */
export function filledArray3D<T>(
  depth: number,
  rows: number,
  cols: number,
  fillCb: (depth: number, row: number, col: number) => T,
): T[][][] {
  const result: T[][][] = [];
  for (let d = 0; d < depth; d++) {
    result.push(filledArray2D(rows, cols, (r, c) => fillCb(d, r, c)));
  }
  return result;
}

/**
 * Creates a typeguard function for the given string enum.
 *
 * There is as weird quirk in the typescript implementation for enums that contains numbers as values,
 * where there is a double mapping (key->value and value->key).
 * For example, if you had an enum like this:
 *
 * enum TestEnum {
 *   "Key" = "Value",
 *   "NumberKey" = 123,
 * }
 *
 * Then TestEnum[123] would be "NumberKey".
 * For that reason, this typeguard only handles string enums.
 *
 * N.B: This function works with Records technically but it's not its intended use case.
 */
export function stringEnumTypeguard<E extends {[key: string]: string}>(
  enumObj: E,
): (value: unknown) => value is E[keyof E] {
  return (value: unknown): value is E[keyof E] =>
    typeof value === 'string' && Object.values(enumObj).includes(value);
}

/**
 * Computes the logarithm of a number in an arbitrary base.
 */
export function logBaseB(x: number, base: number): number {
  return Math.log(x) / Math.log(base);
}

/**
 * Creates a mapped type that obtains the keys of an object whose values are of the given type.
 */
export type GetTypedKeys<Obj, Type> = {
  [Prop in keyof Obj]: Obj[Prop] extends Type ? Prop : never;
}[keyof Obj];
